import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, ArrowRight } from "lucide-react";

const DEPTS = [
  { v: "raw_wood_store", l: "Raw Wood Store" },
  { v: "hardware_store", l: "Hardware Store" },
  { v: "polish_store", l: "Polish Store" },
  { v: "assembly", l: "Assembly" },
  { v: "packing", l: "Packing" },
  { v: "contract_worker", l: "Contract Worker" },
];
const DEPT_L = Object.fromEntries(DEPTS.map((d) => [d.v, d.l]));
const empty = { title: "", product: "", department: "raw_wood_store", assigned_to: "", quantity: 1, priority: "medium", instructions: "", due_date: "", status: "pending" };
const NEXT = { pending: "in_progress", in_progress: "completed", completed: "pending" };

export default function Operations() {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/operations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/operations", { ...form, quantity: Number(form.quantity) });
      toast.success("Work order dispatched");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const advance = async (o) => {
    const { data } = await api.patch(`/operations/${o.id}/status`, { status: NEXT[o.status] });
    setItems((prev) => prev.map((x) => (x.id === o.id ? data : x)));
  };
  const remove = async (id) => { await api.delete(`/operations/${id}`); toast.success("Deleted"); load(); };

  const shown = filter === "all" ? items : items.filter((i) => i.department === filter);

  return (
    <div className="space-y-8">
      <PageHeader title="Operations" subtitle="Dispatch work orders to internal stores and contract workers." icon={ClipboardList}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-op-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px"><Plus className="h-4 w-4" /> New Work Order</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Dispatch Work Order</DialogTitle></DialogHeader>
              <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
                <div className="space-y-2"><Label>Task title</Label>
                  <Input data-testid="op-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Cut & plane table legs" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Department</Label>
                    <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                      <SelectTrigger data-testid="op-dept"><SelectValue /></SelectTrigger>
                      <SelectContent>{DEPTS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Product</Label>
                    <Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Dining Table" /></div>
                  <div className="space-y-2"><Label>Quantity</Label>
                    <Input type="number" data-testid="op-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
                  <div className="col-span-2 space-y-2"><Label>Assigned to / Contractor</Label>
                    <Input data-testid="op-assigned" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} placeholder="Suresh / Verma Polishers" /></div>
                  <div className="col-span-2 space-y-2"><Label>Due date</Label>
                    <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Instructions</Label>
                  <Textarea data-testid="op-instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Specific instructions for the team…" /></div>
              </div>
              <DialogFooter><Button data-testid="op-save" onClick={create} disabled={!form.title}>Dispatch</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="flex flex-wrap gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>All</FilterChip>
        {DEPTS.map((d) => <FilterChip key={d.v} active={filter === d.v} onClick={() => setFilter(d.v)}>{d.l}</FilterChip>)}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No work orders" hint="Dispatch a new work order to a store or contractor." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((o) => (
            <Card key={o.id} data-testid={`op-card-${o.id}`} className="animate-fade-up flex flex-col rounded-md border-border p-5 shadow-none">
              <div className="flex items-start justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">{DEPT_L[o.department]}</span>
                <StatusBadge status={o.status} />
              </div>
              <h3 className="mt-2 font-heading text-base font-bold leading-snug">{o.title}</h3>
              {o.product && <p className="text-xs text-muted-foreground">{o.product} · {o.quantity} units</p>}
              {o.assigned_to && <p className="mt-2 text-sm"><span className="text-muted-foreground">Assigned:</span> {o.assigned_to}</p>}
              {o.instructions && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{o.instructions}</p>}
              <div className="mt-auto flex items-center gap-2 pt-4">
                <Button size="sm" variant="outline" data-testid={`op-advance-${o.id}`} onClick={() => advance(o)} className="flex-1 gap-1">
                  <ArrowRight className="h-3.5 w-3.5" /> {o.status === "completed" ? "Reopen" : `Mark ${NEXT[o.status].replace("_", " ")}`}
                </Button>
                {canManage && (
                  <Button size="icon" variant="ghost" data-testid={`op-delete-${o.id}`} onClick={() => remove(o.id)}
                    className="h-9 w-9 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const FilterChip = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${
    active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}>
    {children}
  </button>
);
